import React from "react";
import { PublicDocShell } from "../components/PublicDocShell";
import "./PrivacyPolicyPage.css";

export const PrivacyPolicyPage: React.FC = () => {
  return (
    <PublicDocShell
      eyebrow="Legal & Compliance"
      title={
        <>
          Privacy <span className="public-doc-title-gradient">Policy</span>
        </>
      }
      subtitle="Last updated: December 28, 2024. This Privacy Policy describes how Kairos collects, uses, and protects your information."
    >
      <div className="privacy-content">
        {/* Introduction */}
        <div className="privacy-section">
          <h2 className="privacy-section-title">
            <svg
              className="privacy-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
            1. Introduction and Scope
          </h2>
          <div className="privacy-section-text">
            <p>
              Kairos ("Company," "we," "us," or "our") is committed to
              protecting your privacy and ensuring the security of your personal
              information. This Privacy Policy ("Policy") governs the
              collection, use, disclosure, and protection of information
              collected through our website, browser extension, mobile
              applications, and services (collectively, "Services").
            </p>
            <p>
              This Policy applies to all users of our Services, including
              visitors to our website, users of our AI-powered job search and
              career optimization platform, and customers of our products and
              services. By using our Services, you acknowledge that you have
              read and understood this Policy and agree to the collection, use,
              and disclosure of your information as described herein.
            </p>
            <p>
              <strong>Important Notice:</strong> This Policy constitutes a
              legally binding agreement between you and Kairos. Your continued
              use of our Services after any changes to this Policy constitutes
              acceptance of those changes.
            </p>
          </div>
        </div>

        {/* Information Collection */}
        <div className="privacy-section">
          <h2 className="privacy-section-title">
            <svg
              className="privacy-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
            2. Information We Collect
          </h2>
          <div className="privacy-section-text">
            <h3 className="privacy-subsection-title">
              2.1 Personal Information
            </h3>
            <p>
              We may collect the following categories of personal information:
            </p>
            <ul className="privacy-list">
              <li>
                <strong>Identifiers:</strong> Name, email address, phone number,
                and unique identifiers
              </li>
              <li>
                <strong>Professional Information:</strong> Job title, company,
                industry, work experience, skills, qualifications, resume
                content, and job application data
              </li>
              <li>
                <strong>Account Information:</strong> Username, password,
                account preferences, service plan details, and security
                questions
              </li>
              <li>
                <strong>Communication Data:</strong> Messages, feedback, support
                requests, and other communications with us
              </li>
              <li>
                <strong>Browser Extension Data:</strong> Job descriptions
                viewed, application status, and interaction data from job boards
                when using our browser extension
              </li>
              <li>
                <strong>Payment Information:</strong> When you register for an
                account, a corresponding account is automatically created in the
                Stripe payment processing system. Stripe collects and processes
                payment information, including credit card details, billing
                addresses, and transaction history, in accordance with Stripe's
                Privacy Policy. We do not store your full credit card
                information on our servers; all payment processing is handled
                securely by Stripe.
              </li>
            </ul>

            <h3 className="privacy-subsection-title">
              2.2 Technical Information
            </h3>
            <p>
              We automatically collect certain technical information when you
              use our Services:
            </p>
            <ul className="privacy-list">
              <li>
                <strong>Device Information:</strong> IP address, device type,
                operating system, browser type, and device identifiers
              </li>
              <li>
                <strong>Usage Data:</strong> Pages visited, features used, time
                spent on Services, job searches performed, applications tracked,
                and interaction patterns
              </li>
              <li>
                <strong>Log Data:</strong> Server logs, error reports, and
                performance metrics
              </li>
              <li>
                <strong>Cookies and Similar Technologies:</strong> As described
                in our Cookie Policy
              </li>
            </ul>

            <h3 className="privacy-subsection-title">
              2.3 AI and Machine Learning Data
            </h3>
            <p>
              As an AI-powered job search and career optimization platform, we
              may collect and process data to improve our artificial
              intelligence systems:
            </p>
            <ul className="privacy-list">
              <li>
                <strong>Training Data:</strong> Anonymized and aggregated data
                used to train and improve our AI models for resume analysis, job
                matching, and interview preparation
              </li>
              <li>
                <strong>User Interactions:</strong> How users interact with our
                AI features, including resume analysis results, job matching
                scores, and interview preparation usage
              </li>
              <li>
                <strong>Performance Metrics:</strong> Data related to the
                accuracy and effectiveness of our AI systems in matching resumes
                to job descriptions and providing career insights
              </li>
            </ul>
            <p>
              <strong>Third-Party AI Services:</strong> Our AI-powered features
              utilize third-party artificial intelligence and machine learning
              services. When you use these features, your data (including
              resumes, job descriptions, application information, and other
              content) is transmitted to and processed by these third-party AI
              service providers. Cognixis AI is not responsible for, and
              disclaims all liability related to, any information collected,
              processed, stored, or used by third-party AI service providers.
              This includes data collection practices, data retention policies,
              data security measures, training data usage, and any other use of
              your information by AI service providers. You acknowledge that by
              using our AI-powered features, your information may be subject to
              the privacy policies, terms of service, and data handling
              practices of these third-party AI providers, which are beyond our
              direct control.
            </p>
            <p>
              <strong>AI Data Collection Disclaimer:</strong> Third-party AI
              service providers may collect, process, and use your information
              for various purposes, including but not limited to model training,
              service improvement, and analytics. Cognixis AI does not control,
              monitor, or assume responsibility for how third-party AI providers
              collect, use, or protect your information. The specific AI service
              providers we utilize may change from time to time, and we are not
              obligated to disclose the identity of these providers or their
              specific data handling practices.
            </p>
            <p>
              <strong>Privacy Protection Measures:</strong> Cognixis AI
              implements comprehensive privacy protection measures and utilizes
              available configuration settings designed to prevent the use of
              customer data for AI model training, data sharing, or other
              purposes beyond the direct provision of our Services. We employ
              industry-standard privacy controls, data processing agreements,
              and technical safeguards to protect your information. However, we
              cannot guarantee or warrant that third-party AI service providers
              will fully comply with these settings or restrictions, as their
              data handling practices are beyond our direct control. While we
              take all reasonable steps to protect your privacy, you acknowledge
              that absolute control over third-party AI provider data practices
              cannot be guaranteed.
            </p>
          </div>
        </div>

        {/* Information Use */}
        <div className="privacy-section">
          <h2 className="privacy-section-title">
            <svg
              className="privacy-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
            3. How We Use Your Information
          </h2>
          <div className="privacy-section-text">
            <p>We use the information we collect for the following purposes:</p>
            <ul className="privacy-list">
              <li>
                <strong>Service Provision:</strong> To provide, maintain, and
                improve our Services, including resume analysis, job matching,
                application tracking, and interview preparation
              </li>
              <li>
                <strong>AI Enhancement:</strong> To train, develop, and improve
                our artificial intelligence systems for better job matching,
                resume optimization, and career insights
              </li>
              <li>
                <strong>Personalization:</strong> To customize your experience,
                provide relevant job recommendations, and tailor interview
                preparation materials
              </li>
              <li>
                <strong>Communication:</strong> To respond to your inquiries,
                provide customer support, and send service-related notifications
              </li>
              <li>
                <strong>Security:</strong> To protect against fraud, abuse, and
                security threats
              </li>
              <li>
                <strong>Legal Compliance:</strong> To comply with applicable
                laws, regulations, and legal processes
              </li>
              <li>
                <strong>Business Operations:</strong> To conduct business
                analytics, improve our operations, and manage service plans and
                subscriptions
              </li>
              <li>
                <strong>Browser Extension Functionality:</strong> To enable the
                browser extension to analyze job postings, match resumes, and
                track applications on job boards
              </li>
            </ul>
            <p>
              <strong>Legal Basis:</strong> We process your information based on
              legitimate business interests, contractual obligations, legal
              requirements, and your consent where applicable.
            </p>
          </div>
        </div>

        {/* Information Sharing */}
        <div className="privacy-section">
          <h2 className="privacy-section-title">
            <svg
              className="privacy-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
            4. Information Sharing and Disclosure
          </h2>
          <div className="privacy-section-text">
            <p>
              We do not sell, rent, or trade your personal information to third
              parties. However, we may share your information in the following
              circumstances:
            </p>
            <ul className="privacy-list">
              <li>
                <strong>Service Providers:</strong> With trusted third-party
                service providers who assist us in operating our Services,
                including cloud hosting (AWS), payment processing (Stripe), and
                analytics services
              </li>
              <li>
                <strong>AI Service Providers:</strong> With third-party
                artificial intelligence and machine learning service providers
                to process your data for AI-powered features such as resume
                analysis, job matching, interview preparation, and company
                intelligence. These AI providers may collect, process, and store
                your information in accordance with their own privacy policies
                and terms of service. The specific AI service providers we
                utilize may change from time to time, and we are not obligated
                to disclose the identity of these providers
              </li>
              <li>
                <strong>Legal Requirements:</strong> When required by law,
                regulation, or legal process
              </li>
              <li>
                <strong>Business Transfers:</strong> In connection with a
                merger, acquisition, or sale of assets
              </li>
              <li>
                <strong>Protection of Rights:</strong> To protect our rights,
                property, or safety, or that of our users
              </li>
              <li>
                <strong>Consent:</strong> With your explicit consent or at your
                direction
              </li>
              <li>
                <strong>Job Board Integration:</strong> When using our browser
                extension, job descriptions and application data may be
                processed to provide matching and tracking features
              </li>
            </ul>
            <p>
              <strong>Data Processing Agreements:</strong> All third-party
              service providers are bound by strict data processing agreements
              and security requirements. We ensure that service providers like
              Stripe (for payment processing) and AWS (for cloud infrastructure)
              maintain appropriate security measures. For AI service providers,
              we implement privacy protection measures and utilize available
              configuration settings designed to prevent the use of customer
              data for AI model training or data sharing. However, we are not
              responsible for the privacy practices, data handling, or security
              measures of third-party AI service providers, and you acknowledge
              that your information may be subject to the privacy policies and
              terms of service of these AI providers. The specific AI service
              providers we utilize may change from time to time, and we are not
              obligated to disclose the identity of these providers. While we
              take all reasonable steps to protect your privacy through
              technical safeguards and contractual agreements, we cannot
              guarantee that third-party AI providers will fully comply with
              these restrictions, as their data handling practices are beyond
              our direct control.
            </p>
            <p>
              <strong>Stripe Account Creation:</strong> When you register for a
              Kairos account, a corresponding customer account is automatically
              created in the Stripe payment processing system. This Stripe
              account is used exclusively for managing your subscription
              payments and billing. Stripe collects and processes your payment
              information in accordance with Stripe's Privacy Policy and Terms
              of Service. You may access your Stripe account information through
              your Kairos account settings or directly through Stripe's customer
              portal. We share with Stripe only the information necessary to
              process payments and manage your subscription.
            </p>
          </div>
        </div>

        {/* Data Security */}
        <div className="privacy-section">
          <h2 className="privacy-section-title">
            <svg
              className="privacy-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
            5. Data Security and Protection
          </h2>
          <div className="privacy-section-text">
            <p>
              We implement comprehensive security measures to protect your
              information:
            </p>
            <ul className="privacy-list">
              <li>
                <strong>Encryption:</strong> All data is encrypted in transit
                and at rest using industry-standard encryption protocols (TLS
                1.2+ for data in transit, AES-256 for data at rest)
              </li>
              <li>
                <strong>Access Controls:</strong> Strict access controls and
                authentication mechanisms, including JWT-based authentication
                and role-based access control
              </li>
              <li>
                <strong>Security Monitoring:</strong> Continuous monitoring and
                threat detection systems
              </li>
              <li>
                <strong>Regular Audits:</strong> Periodic security assessments
                and penetration testing
              </li>
              <li>
                <strong>Employee Training:</strong> Regular security training
                for all employees
              </li>
              <li>
                <strong>AWS Security:</strong> Our infrastructure uses AWS
                security best practices, including VPC isolation, security
                groups, and encrypted storage
              </li>
            </ul>
            <p>
              <strong>Data Breach Response:</strong> In the unlikely event of a
              data breach, we will notify affected users and relevant
              authorities as required by law, typically within 72 hours of
              becoming aware of the breach.
            </p>
          </div>
        </div>

        {/* User Rights */}
        <div className="privacy-section">
          <h2 className="privacy-section-title">
            <svg
              className="privacy-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
            6. Your Rights and Choices
          </h2>
          <div className="privacy-section-text">
            <p>You have certain rights regarding your personal information:</p>
            <ul className="privacy-list">
              <li>
                <strong>Access:</strong> Request access to your personal
                information, including resumes, job applications, and analysis
                data
              </li>
              <li>
                <strong>Correction:</strong> Request correction of inaccurate
                information, such as updating your profile or resume details
              </li>
              <li>
                <strong>Deletion:</strong> Request deletion of your personal
                information, including account deletion and removal of stored
                resumes
              </li>
              <li>
                <strong>Portability:</strong> Request a copy of your data in a
                portable format (e.g., export your job applications and resume
                data)
              </li>
              <li>
                <strong>Restriction:</strong> Request restriction of processing
                in certain circumstances
              </li>
              <li>
                <strong>Objection:</strong> Object to certain types of
                processing, such as marketing communications
              </li>
              <li>
                <strong>Opt-Out:</strong> Opt out of certain data collection
                practices, such as analytics cookies (see our Cookie Policy)
              </li>
            </ul>
            <p>
              <strong>Exercise of Rights:</strong> To exercise these rights,
              contact us at support@cognixisai.com. We will respond to your
              request within 30 days. For account deletion, you can also use the
              account deletion feature in your Settings page.
            </p>
          </div>
        </div>

        {/* International Transfers */}
        <div className="privacy-section">
          <h2 className="privacy-section-title">
            <svg
              className="privacy-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            7. International Data Transfers
          </h2>
          <div className="privacy-section-text">
            <p>
              Your information may be transferred to and processed in countries
              other than your own. We ensure that such transfers comply with
              applicable data protection laws through:
            </p>
            <ul className="privacy-list">
              <li>
                <strong>Adequacy Decisions:</strong> Transfers to countries with
                adequate data protection
              </li>
              <li>
                <strong>Standard Contractual Clauses:</strong> EU-approved data
                transfer agreements
              </li>
              <li>
                <strong>Certification Schemes:</strong> Industry-recognized
                privacy certifications
              </li>
              <li>
                <strong>Consent:</strong> Your explicit consent where required
              </li>
              <li>
                <strong>AWS Data Centers:</strong> Our data is stored in AWS
                data centers, which maintain compliance with various
                international data protection standards
              </li>
            </ul>
            <p>
              <strong>Data Residency:</strong> By default, your data is stored
              in AWS data centers in the United States. If you are located in
              the EU, you may request that your data be processed in EU-based
              data centers where available.
            </p>
          </div>
        </div>

        {/* Children's Privacy */}
        <div className="privacy-section">
          <h2 className="privacy-section-title">
            <svg
              className="privacy-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              />
            </svg>
            8. Children's Privacy
          </h2>
          <div className="privacy-section-text">
            <p>
              Our Services are not intended for children under the age of 18. We
              do not knowingly collect personal information from children under
              18. If you become aware that a child has provided us with personal
              information, please contact us immediately at
              support@cognixisai.com, and we will take steps to delete such
              information.
            </p>
            <p>
              <strong>Age Verification:</strong> By using our Services, you
              represent and warrant that you are at least 18 years old and have
              the legal capacity to enter into binding agreements.
            </p>
          </div>
        </div>

        {/* Changes to Policy */}
        <div className="privacy-section">
          <h2 className="privacy-section-title">
            <svg
              className="privacy-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            9. Changes to This Privacy Policy
          </h2>
          <div className="privacy-section-text">
            <p>
              We may update this Privacy Policy from time to time. We will
              notify you of any material changes by:
            </p>
            <ul className="privacy-list">
              <li>Posting the updated Policy on our website</li>
              <li>Sending you an email notification</li>
              <li>Displaying a prominent notice on our Services</li>
            </ul>
            <p>
              <strong>Continued Use:</strong> Your continued use of our Services
              after any changes constitutes acceptance of the updated Policy.
            </p>
            <p>
              <strong>Review:</strong> We encourage you to review this Policy
              periodically to stay informed about how we collect, use, and
              protect your information.
            </p>
          </div>
        </div>

        {/* Contact Information */}
        <div className="privacy-section">
          <h2 className="privacy-section-title">
            <svg
              className="privacy-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            10. Contact Us
          </h2>
          <div className="privacy-section-text">
            <p>
              If you have any questions about this Privacy Policy or our data
              practices, please contact us:
            </p>
            <div className="privacy-contact-box">
              <p>
                <strong>Email:</strong> support@cognixisai.com
              </p>
              <p>
                <strong>Phone:</strong> (801) 306-5060
              </p>
            </div>
            <p>
              <strong>Data Protection Officer:</strong> For EU residents, you
              may also contact our Data Protection Officer at
              legal@cognixisai.com.
            </p>
            <p>
              <strong>Privacy Requests:</strong> For requests related to your
              privacy rights (access, deletion, portability, etc.), please email
              support@cognixisai.com with "Privacy Request" in the subject line.
            </p>
          </div>
        </div>

        {/* Legal Disclaimer */}
        <div className="privacy-section privacy-section-highlight-warning">
          <h2 className="privacy-section-title">
            <svg
              className="privacy-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
            Legal Disclaimer
          </h2>
          <div className="privacy-section-text">
            <p>
              <strong>Important Legal Notice:</strong> This Privacy Policy is a
              legally binding document that governs the relationship between you
              and Kairos. By using our Services, you acknowledge that you have
              read, understood, and agreed to be bound by the terms of this
              Policy.
            </p>
            <p>
              <strong>Governing Law:</strong> This Policy is governed by and
              construed in accordance with the laws of the State of Utah, United
              States, without regard to conflict of law principles.
            </p>
            <p>
              <strong>Severability:</strong> If any provision of this Policy is
              found to be unenforceable, the remaining provisions will continue
              in full force and effect.
            </p>
            <p>
              <strong>Entire Agreement:</strong> This Policy, together with our
              Terms of Service and Cookie Policy, constitutes the entire
              agreement between you and Kairos regarding the collection and use
              of your information.
            </p>
            <p>
              <strong>Legal Advice:</strong> This Policy is not intended to
              constitute legal advice. If you have questions about your legal
              rights, please consult with a qualified attorney.
            </p>
          </div>
        </div>
      </div>
    </PublicDocShell>
  );
};
